<template>
  <div class="w-full relative">
    <div v-if="label">
      <label
        :for="label"
        class="text-text-primary text-sm font-medium dark:text-dark-secondary"
        :class="[
          labelClass,
          allowLabelWrap ? 'whitespace-normal' : 'whitespace-nowrap',
        ]"
      >
        {{ label }}
        <span v-if="required" class="ml-1">
          (Obligatorio)<span class="text-text-error">*</span>
        </span>
      </label>
    </div>

    <Select
      v-model="model"
      :options="internalOptions"
      :optionLabel="optionLabel"
      :placeholder="placeholder"
      :size="size"
      :optionValue="optionValue"
      :filter="filter"
      :filterMatchMode="filterMatchMode"
      :disabled="disabled"
      class="w-full dark:bg-surface-primary!"
      :class="[selectClass, { 'border-helper-text!': hasError }]"
      :aria-invalid="hasError"
      :aria-describedby="errorId"
      @change="handleChange($event)"
      @blur="handleBlur"
    >
      <template #value="slotProps">
        <div
          v-if="
            customOption &&
            slotProps?.value !== null &&
            slotProps?.value !== undefined
          "
          class="flex items-center"
        >
          <div>{{ slotProps.value.name }}</div>
        </div>
      </template>

      <template #option="slotProps">
        <div v-if="customOption && slotProps?.option" class="flex items-center">
          <font-awesome-icon
            v-if="slotProps.option.blockType === 'Sujeto a Evaluación'"
            icon="fa-solid fa-exclamation-triangle"
            class="text-icon-warning dark:text-dark-secondary text-[11px]"
          />
          <div
            class="ml-2 text-text-secondary text-[14px] font-400 leading-6"
            :class="[
              { 'ml-5': slotProps.option.blockType === 'Sin Restricción' },
            ]"
          >
            {{ slotProps.option.name }}
          </div>
        </div>
      </template>
    </Select>

    <!-- Contenedor fijo para el mensaje de error -->
    <div class="relative h-4 mt-1">
      <transition name="fade">
        <p
          v-if="displayError"
          class="absolute left-0 top-0 text-helper-text text-[11px] leading-3 flex items-center gap-1"
          :id="errorId"
        >
          <font-awesome-icon
            icon="fa-solid fa-circle-xmark"
            class="text-helper-text mr-1"
          />
          {{ displayError }}
        </p>
        <p
          v-else-if="helper"
          class="absolute left-0 top-0 text-text-secondary text-[11px] leading-3 flex items-center gap-"
        >
          <font-awesome-icon
            icon="fa-solid fa-circle-info"
            class="text-[11px] text-accent-sky-60 font-900 align-center leading-3"
            :class="{ 'text-text-secondary': disabled }"
          />
          {{ helper }}
        </p>
      </transition>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from "vue";

type CountryOption =
  | {
      name: string;
      blockType?: string;
      [key: string]: any;
    }
  | string
  | number
  | null;

const model = defineModel<CountryOption>();

const props = defineProps({
  options: {
    type: Array,
    default: [],
  },
  optionLabel: {
    type: String,
    default: null,
  },
  optionValue: {
    type: String,
    default: null,
  },
  placeholder: {
    type: String,
    default: "Seleccione una opción",
  },
  selectClass: {
    type: String,
    default: "",
  },
  labelClass: {
    type: String,
    default: "",
  },
  size: {
    type: String,
    default: "",
  },
  label: {
    type: String,
    required: false,
    default: "",
  },
  required: {
    type: Boolean,
    default: true,
  },
  filter: {
    type: Boolean,
    default: false,
  },
  filterMatchMode: {
    type: String as () => "startsWith" | "contains" | "endsWith",
    default: "startsWith",
    validator: (value: string) => ["startsWith", "contains", "endsWith"].includes(value),
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  customOption: {
    type: Boolean,
    default: false,
    required: false,
  },
  baseUrl: {
    type: String,
  },
  error: {
    type: String,
    default: "",
  },
  helper: {
    type: String,
    default: "",
  },
  allowLabelWrap: {
    type: Boolean,
    default: false,
  },
  validationMode: {
    type: String as () => "onChange" | "onBlur" | "manual",
    default: "onChange",
    validator: (value: string) =>
      ["onChange", "onBlur", "manual"].includes(value),
  },
});

const emits = defineEmits<{ (e: "change", event: any): void }>();

function areArraysEqual(a: any[], b: any[]): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i++) {
    if (a[i] !== b[i]) return false;
  }
  return true;
}

const internalOptions = ref<any[]>([]);

watch(
  () => props.options,
  (newVal) => {
    const arr = (newVal || []) as any[];
    if (!areArraysEqual(internalOptions.value, arr)) {
      internalOptions.value = arr;
    }
  },
  { immediate: true }
);

const touched = ref(false);
const validationError = ref("");

const hasValue = computed(() => model.value != null && model.value !== "");

function getErrorForValue(): string {
  if (props.required && touched.value && !hasValue.value) {
    return "Este campo es obligatorio";
  }
  return "";
}

function runValidation(): void {
  validationError.value = getErrorForValue();
}

function handleChange(event: any): void {
  touched.value = true;
  emits("change", event);
  if (props.validationMode !== "manual") {
    runValidation();
  }
}

function handleBlur(): void {
  touched.value = true;
  if (props.validationMode !== "manual") {
    runValidation();
  }
}

watch(model, () => {
  if (props.validationMode === "manual") {
    validationError.value = "";
    return;
  }
  if (props.validationMode === "onBlur" && !touched.value) {
    validationError.value = "";
    return;
  }
  if (props.validationMode === "onChange") {
    runValidation();
  }
});

const hasError = computed<boolean>(() =>
  Boolean(props.error || validationError.value),
);
const displayError = computed<string | undefined>(
  () => props.error || validationError.value || undefined,
);
const errorId = computed<string | undefined>(() =>
  props.label ? `${props.label}-error` : undefined,
);
</script>

<style scoped>
.custom-select-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f3f4f6;
  color: #9ca3af;
}

.custom-select-disabled:hover {
  cursor: not-allowed;
}

/* Estilos específicos para el dropdown de PrimeVue cuando está disabled */
:deep(.p-select.p-disabled) {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #f3f4f6 !important;
  color: #9ca3af !important;
}

:deep(.p-select.p-disabled .p-select-label) {
  color: #9ca3af !important;
}

:deep(.p-select.p-disabled .p-select-dropdown) {
  cursor: not-allowed;
}
</style>
